import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faCheck, faXmark, faThumbtack } from "@fortawesome/free-solid-svg-icons";
// The same cell shape as «النسبة»: a value that opens into an input with a
// save and a cancel. The styles are shared, not copied.
import "components/Ui/PercentageCell/percentageCell.scss";

/** A pinned place is a whole number from 1 to 999, the API's limit. */
export const parseDisplayRank = (draft) => {
    const text = String(draft ?? "").trim();
    if (text === "") return { rank: null };
    const rank = Number(text);
    if (!Number.isInteger(rank) || rank < 1 || rank > 999) return { error: true };
    return { rank };
};

/**
 * The place of a restaurant in the customer lists, editable in the table.
 *
 * PINNED AND NOT PINNED MUST LOOK DIFFERENT. Most restaurants have no place of
 * their own: they follow the open ones and the pinned ones, in an order that
 * changes every 30 minutes. Such a row shows «تلقائي», muted. A pinned row shows
 * its number with a pin.
 *
 * An empty box removes the pin. That is a real action, not an error.
 */
const DisplayRankCell = ({ rank, onSave, disabled = false }) => {
    const isPinned = rank !== null && rank !== undefined;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) inputRef.current.focus();
    }, [editing]);

    const cancel = () => {
        setEditing(false);
        setDraft("");
    };

    const save = async () => {
        const parsed = parseDisplayRank(draft);
        if (parsed.error) return;
        setSaving(true);
        try {
            await onSave(parsed.rank);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <td className="percentage-cell">
                <div className="percentage-cell__edit">
                    <input
                        ref={inputRef}
                        type="number"
                        min="1"
                        max="999"
                        step="1"
                        inputMode="numeric"
                        value={draft}
                        disabled={saving}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") save();
                            if (e.key === "Escape") cancel();
                        }}
                        placeholder="فارغ = تلقائي"
                        aria-label="الترتيب"
                    />
                    <button type="button" onClick={save} disabled={saving} title="حفظ">
                        <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button type="button" onClick={cancel} disabled={saving} title="إلغاء">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            </td>
        );
    }

    return (
        <td className="percentage-cell">
            <button
                type="button"
                className={`percentage-cell__value${isPinned ? "" : " percentage-cell__value--default"}`}
                onClick={() => {
                    setDraft(isPinned ? String(rank) : "");
                    setEditing(true);
                }}
                disabled={disabled}
                title={
                    isPinned
                        ? "مثبّت في أول القوائم — اضغط لتغيير مكانه أو إزالة التثبيت"
                        : "غير مثبّت — ترتيبه يتغير كل 30 دقيقة. اضغط لتثبيته"
                }
            >
                {isPinned ? (
                    <span className="percentage-cell__number">
                        <FontAwesomeIcon
                            icon={faThumbtack}
                            size="xs"
                            className="percentage-cell__mark"
                        />{" "}
                        {rank}
                    </span>
                ) : (
                    <span className="percentage-cell__badge">تلقائي</span>
                )}
                <FontAwesomeIcon icon={faPen} size="xs" />
            </button>
        </td>
    );
};

export default DisplayRankCell;
